import { Box, Divider, Pagination, Typography } from '@mui/material';
import { paths } from '@common/types';
import { Check } from '@mui/icons-material';
import { ceil } from 'lodash';
import { HistoryUserPopover } from './components';
import { Fragment } from 'react';
import { formatDate } from '@util/date.ts';
import { theme } from '@store';

export type ComposedHistoryType =
  | paths[`/task/{task_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/building/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/operator/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/user/{user_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/team/{team_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/provider/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/practice/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/partner/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths[`/organization/{record_id}/history`]['get']['responses']['200']['content']['application/json']['history']
  | paths['/patient/{patient_id}/history']['get']['responses']['200']['content']['application/json']['history'];

interface IHistoryProps {
  history: ComposedHistoryType;
  count: paths[`/task/{task_id}/history`]['get']['responses']['200']['content']['application/json']['count'];
  page: number;
  changePage: (page: number) => void;
  additionalActionButton?: React.ReactNode;
  handleOpenTaskDetails?: (id: string) => void;
}

export const History = ({
  history,
  count,
  page,
  changePage,
  additionalActionButton,
  handleOpenTaskDetails,
}: IHistoryProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
      }}
    >
      {count === 0 ? (
        <Box sx={{ display: 'grid', placeItems: 'center', height: '300px' }}>
          <Typography
            variant='body1'
            sx={{
              color: theme => theme.palette.semiBlack.main,
              textAlign: 'center',
            }}
          >
            No history available yet.
          </Typography>
        </Box>
      ) : (
        <Box>
          {history.map((entry, index) => {
            return (
              <Fragment key={`${entry.description} + ${index}`}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      gap: '1rem',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: theme => theme.palette.primary.main,
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                      }}
                    >
                      <Check
                        sx={{
                          fontSize: '1.25rem',
                          color: theme => theme.palette.common.white,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <EntryDescription
                        entry={entry}
                        handleOpenTaskDetails={handleOpenTaskDetails}
                      />
                      <Typography
                        variant='caption'
                        sx={{ color: theme => theme.palette.semiBlack.main }}
                      >
                        {formatDate(entry.date)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {index !== history.length - 1 && (
                  <Divider
                    orientation='vertical'
                    sx={{
                      width: '.825rem',
                      borderRightWidth: '2px',
                      height: '1.5rem',
                      borderColor: theme => theme.palette.primary.main,
                    }}
                  />
                )}
              </Fragment>
            );
          })}
        </Box>
      )}

      {count > 10 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: '-1px',
            padding: '24px 0',
            left: 0,
            background: theme.palette.common.white,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Pagination
            count={ceil(count / 10)}
            page={page}
            onChange={(e, number) => changePage(number)}
            showFirstButton
            showLastButton
          />
          {additionalActionButton}
        </Box>
      )}
      {count < 11 && additionalActionButton && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingY: 3,
            position: 'sticky',
            bottom: 0,
            left: 0,
          }}
        >
          {additionalActionButton}
        </Box>
      )}
    </Box>
  );
};

interface IEntryDescription {
  entry: ComposedHistoryType[0];
  handleOpenTaskDetails?: (id: string) => void;
}

const EntryDescription = ({
  entry,
  handleOpenTaskDetails,
}: IEntryDescription) => {
  const variables = [...entry.variables];

  const regex = /{{(\w+)}}/g;

  const text = entry.description;
  const descriptionParts = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      descriptionParts.push(text.slice(lastIndex, match.index));
    }
    descriptionParts.push(match[1]);

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    descriptionParts.push(text.slice(lastIndex));
  }

  const mappedParts = descriptionParts.map(part => {
    const parsedPart = part.replace('{', '');
    const variable = variables.find(
      variable => variable.variable === parsedPart,
    );
    if (variable) {
      const index = variables.indexOf(variable);
      variables.splice(index, 1);
      return `{${variable?.name}`;
    }
    return parsedPart;
  });

  return (
    <Box>
      {mappedParts.map((part, index) => {
        const formatedText = part.replace('{', '');

        const user = entry.variables.find(variable => {
          return variable.name === formatedText && variable.type === 'user';
        });

        const task = entry.variables.find(variable => {
          return variable.type === 'task';
        });

        return (
          <Fragment key={`${part}+${index}`}>
            {part.includes('{') ? (
              <Fragment>
                &nbsp;
                {user && user.type === 'user' ? (
                  <HistoryUserPopover user={user} />
                ) : (
                  <Typography
                    component='span'
                    variant='body2'
                    onClick={() => {
                      if (task && handleOpenTaskDetails) {
                        handleOpenTaskDetails(task.id);
                      }
                    }}
                    sx={{
                      cursor: task ? 'pointer' : 'unset',
                      textDecoration: task ? 'underline' : 'unset',
                      color: task
                        ? theme => theme.palette.primary.main
                        : 'text.primary',
                      textDecorationColor: task
                        ? theme => theme.palette.primary.dark
                        : 'unset',
                    }}
                  >
                    {formatedText}
                  </Typography>
                )}
                &nbsp;
              </Fragment>
            ) : (
              <Typography component='span' variant='body2'>
                {part}
              </Typography>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};
